import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, KeyRound, LockOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmtDT } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, tableWrap, th, td } from "@/components/common";

function NewUser({ roles, onClose, onSaved }) {
  const [f, setF] = useState({ email: "", name: "", role: "warehouse_staff", password: "", mobile: "", department: "", designation: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try { await api.post("/admin/users", f); toast.success("User created. Share the temporary password securely."); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="user-form-dialog">
        <DialogHeader><DialogTitle>Add staff login</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Name" required><Input value={f.name} onChange={set("name")} data-testid="user-name-input" /></Field>
          <Field label="Email" required><Input type="email" value={f.email} onChange={set("email")} data-testid="user-email-input" /></Field>
          <Field label="Role" required><Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></Field>
          <Field label="Temporary password (min 8)" required><Input value={f.password} onChange={set("password")} data-testid="user-password-input" /></Field>
          <Field label="Mobile"><Input value={f.mobile} onChange={set("mobile")} data-testid="user-mobile-input" /></Field>
          <Field label="Department"><Input value={f.department} onChange={set("department")} data-testid="user-department-input" /></Field>
          <Field label="Designation"><Input value={f.designation} onChange={set("designation")} data-testid="user-designation-input" /></Field>
        </div>
        <DialogFooter><Button onClick={save} data-testid="user-save-btn">Create user</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [add, setAdd] = useState(false);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); api.get("/admin/roles").then((r) => setRoles(r.data)); }, []);
  const patch = async (id, body) => { try { await api.patch(`/admin/users/${id}`, body); toast.success("Updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const resetPw = async (u) => {
    if (!window.confirm(`Issue a new temporary password for ${u.email}? Their current sessions will end.`)) return;
    try { const { data } = await api.post(`/admin/users/${u.id}/reset-password`); window.prompt("Temporary password (copy and share securely):", data.temporary_password); } catch (e) { toast.error(errMsg(e)); }
  };
  const unlock = async (u) => {
    try { await api.post(`/admin/users/${u.id}/unlock`); toast.success(`${u.name} can sign in again now`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button onClick={() => setAdd(true)} data-testid="user-add-btn"><Plus className="h-4 w-4 mr-1" />Add user</Button></div>
      <div className={tableWrap}>
        <table className="w-full" data-testid="users-table">
          <thead><tr>{["Name", "Email", "Role", "Last login", "Active", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
          <tbody>{users.map((u) => (
            <tr key={u.id} data-testid={`user-row-${u.email}`}>
              <td className={td}>{u.name}
                {u.locked && <span className="ml-2 inline-flex rounded bg-red-600/10 px-1.5 py-0.5 text-[11px] font-semibold text-red-600" data-testid={`user-locked-${u.email}`}>Locked until {fmtDT(u.locked_until).split(", ").pop()}</span>}
                {!u.locked && u.failed_attempts > 0 && <span className="ml-2 text-[11px] text-amber-600" data-testid={`user-failed-${u.email}`}>{u.failed_attempts} wrong tr{u.failed_attempts === 1 ? "y" : "ies"}</span>}
              </td><td className={td}>{u.email}</td>
              <td className={td}>
                <Select value={u.role} onValueChange={(v) => patch(u.id, { role: v })} disabled={u.id === me.id}>
                  <SelectTrigger className="h-8 w-48" data-testid={`user-role-${u.email}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className={td}>{fmtDT(u.last_login)}</td>
              <td className={td}><Switch checked={u.active} disabled={u.id === me.id} onCheckedChange={(v) => patch(u.id, { active: v })} data-testid={`user-active-${u.email}`} /></td>
              <td className={td}><div className="flex items-center gap-1">
                {(u.locked || u.failed_attempts > 0) && <Button size="sm" variant={u.locked ? "default" : "outline"} className={u.locked ? "bg-red-600 hover:bg-red-700 text-white h-8" : "h-8"} onClick={() => unlock(u)} data-testid={`user-unlock-${u.email}`}><LockOpen className="h-4 w-4 mr-1" />Unlock now</Button>}
                <Button size="icon" variant="ghost" title="Reset password" onClick={() => resetPw(u)} data-testid={`user-reset-pw-${u.email}`}><KeyRound className="h-4 w-4" /></Button>
              </div></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {add && <NewUser roles={roles} onClose={() => setAdd(false)} onSaved={() => { setAdd(false); load(); }} />}
    </div>
  );
}

function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/audit-logs", { params: { q } }).then((r) => setLogs(r.data)); }, [q]);
  return (
    <div className="space-y-3">
      <Input placeholder="Search action, user, detail…" value={q} onChange={(e) => setQ(e.target.value)} className="sm:max-w-sm" data-testid="audit-search-input" />
      <div className={tableWrap}>
        <table className="w-full" data-testid="audit-table">
          <thead><tr>{["Time", "User", "Action", "Entity", "Detail", "IP"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
          <tbody>{logs.map((l) => (
            <tr key={l.id}><td className={td}>{fmtDT(l.at)}</td><td className={td}>{l.user_name}</td><td className={`${td} font-mono text-xs`}>{l.action}</td><td className={td}>{l.entity}</td><td className={`${td} max-w-md truncate`}>{l.detail}</td><td className={`${td} font-mono text-xs`}>{l.ip}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  return (
    <div>
      <PageHeader title="Administration" subtitle="Staff logins, roles and the complete user activity log. Public registration is disabled." />
      <Tabs defaultValue="users">
        <TabsList><TabsTrigger value="users" data-testid="admin-tab-users">Users &amp; roles</TabsTrigger><TabsTrigger value="audit" data-testid="admin-tab-audit">Activity log</TabsTrigger></TabsList>
        <TabsContent value="users" className="mt-4"><Users /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditLogs /></TabsContent>
      </Tabs>
    </div>
  );
}
